import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, setToken, loadToken, currentPortal, apiError } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [config, setConfig] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const portal = currentPortal();
      const t = loadToken(portal);
      if (!t) return setUser(false);
      const { data } = await http.get("/auth/me");
      // A token from another portal must not leak into this one's session
      // (shared-origin dev preview) — surface the real login screen instead.
      if (data.portal !== portal) return setUser(false);
      // Migrate a legacy shared token into the namespaced slot.
      if (!localStorage.getItem(`anand_token_${portal}`)) setToken(t, portal);
      setUser(data);
    } catch {
      // don't clobber an already logged-in user if a stale check resolves late
      setUser((prev) => (prev && prev.portal ? prev : false));
    }
  }, []);

  useEffect(() => {
    http.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    refresh();
  }, [refresh]);

  const login = async (identifier, password, portal) => {
    try {
      portal = portal || currentPortal();
      const { data } = await http.post("/auth/login", { identifier, password, portal });
      setToken(data.token, portal);
      localStorage.removeItem("anand_token"); // drop any cross-portal stale token
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail) };
    }
  };

  const register = async (kind, payload) => {
    try {
      const { data } = await http.post(`/auth/register-${kind}`, payload);
      setToken(data.token, data.user.portal);
      localStorage.removeItem("anand_token");
      setUser(data.user);
      return { ok: true, portal: data.user.portal };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch {}
    setToken(null, currentPortal());
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, config, login, logout, register, refresh, setConfig }}>
      {children}
    </AuthCtx.Provider>
  );
}
